import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import {
  LeadVariable,
  MetaTemplate,
  MetaWhatsappService,
} from 'src/app/shared/services/meta-whatsapp.service';

export interface WhatsappTemplateSelection {
  template_name: string | null;
  template_language: string | null;
  template_variables: string[];
}

/**
 * Escolha do template da Meta para uma sequência de WhatsApp.
 *
 * Fora da janela de 24h a Meta só aceita template aprovado, e o envio proativo por
 * entrada na lista está quase sempre fora dela — então isto não é um extra, é o
 * conteúdo da primeira mensagem.
 *
 * Não escreve no formulário: emite a seleção e quem hospeda decide onde colar. Mesma
 * filosofia do app-prompt-actions, e é o que permite usar com ReactiveForms.
 */
@Component({
  selector: 'app-whatsapp-template-picker',
  templateUrl: './whatsapp-template-picker.component.html',
  styleUrls: ['./whatsapp-template-picker.component.scss'],
})
export class WhatsappTemplatePickerComponent implements OnInit {

  @Input() templateName: string | null = null;
  @Input() templateVariables: string[] = [];

  @Output() changed = new EventEmitter<WhatsappTemplateSelection>();

  approvedTemplates: MetaTemplate[] = [];
  leadVariables: LeadVariable[] = [];
  metaConnected = false;

  /**
   * Templates e conta ainda a caminho (CU-86akkubzz). Sem isto o aviso de
   * "nenhum template aprovado" aparecia antes de a lista chegar — e, sem o
   * overlay global, nada mais dizia que ela estava vindo.
   */
  loadingTemplates = true;
  loadingAccount = true;

  get loading(): boolean {
    return this.loadingTemplates || this.loadingAccount;
  }

  constructor(private metaWhatsapp: MetaWhatsappService) {}

  ngOnInit(): void {
    // Sem conta Meta conectada o endpoint erra; a lista fica vazia e a tela avisa.
    this.metaWhatsapp.listTemplates().subscribe({
      next: list => {
        this.approvedTemplates = (list || []).filter(t => t.status === 'APPROVED');
        this.loadingTemplates = false;
      },
      error: () => (this.loadingTemplates = false),
    });

    this.metaWhatsapp.get().subscribe({
      next: account => {
        this.metaConnected = !!(account && account.has_access_token && account.verified_at);
        this.loadingAccount = false;
      },
      error: () => (this.loadingAccount = false),
    });

    this.metaWhatsapp.leadVariables().subscribe({
      next: list => this.leadVariables = list || [],
      error: () => {},
    });
  }

  /** Corpo do template selecionado, para o preview. */
  get selectedBody(): string {
    const tpl = this.approvedTemplates.find(t => t.name === this.templateName);
    return (tpl?.components || []).find(c => c.type === 'BODY')?.text || '';
  }

  /** Um seletor por placeholder: índice 0 preenche {{1}}, índice 1 preenche {{2}}... */
  get placeholderIndexes(): number[] {
    const count = (this.selectedBody.match(/\{\{(\d+)\}\}/g) || []).length;
    return Array.from({ length: count }, (_, i) => i);
  }

  onTemplateChange(): void {
    if (!this.templateName) {
      this.templateVariables = [];
      this.emit(null);
      return;
    }

    const tpl = this.approvedTemplates.find(t => t.name === this.templateName);
    const count = this.placeholderIndexes.length;

    // Só o {{1}} nasce com o nome do cliente — é o placeholder de abertura de quase todo
    // template. Do {{2}} em diante o palpite erra mais do que acerta, e um select que já
    // vem preenchido não convida a conferir: o cliente só descobriria pela mensagem que
    // chegou ao lead. Os demais ficam vazios e o formulário bloqueia salvar assim.
    this.templateVariables = Array.from(
      { length: count },
      (_, i) => this.templateVariables[i] || (i === 0 ? 'first_name' : '')
    );

    this.emit(tpl?.language || 'pt_BR');
  }

  onVariableChange(): void {
    const tpl = this.approvedTemplates.find(t => t.name === this.templateName);
    this.emit(tpl?.language || 'pt_BR');
  }

  private emit(language: string | null): void {
    this.changed.emit({
      template_name: this.templateName || null,
      template_language: this.templateName ? language : null,
      template_variables: this.templateName ? this.templateVariables : [],
    });
  }
}
